/* eslint-disable jsdoc/require-jsdoc, jsdoc/require-param, jsdoc/require-returns */
import { DEV_NEW_DOMAIN_URL } from '../environment';
import authenticateService from './authenticateService';
import { getTokenFromSessionStorage } from './session-storage-service';

/**
 * Client for the BLE live-ping push stream (`GET /ble-pings/admin/stream`,
 * Server-Sent Events).
 *
 * Why fetch and not `EventSource`: EventSource cannot send an Authorization
 * header, and putting the JWT in the query string would leak it into proxy
 * and access logs. A streamed fetch carries the header like every other call.
 *
 * Reconnects on its own with capped, jittered backoff.
 *
 * A 401/403 first tries ONE token refresh and reconnects. This matters because
 * the stream is the only call on the page that does not go through
 * `_helpers/interceptor`, whose response interceptor refreshes and replays on
 * 401. Without the refresh here, an expired access token looked like a
 * PERMISSION failure that no amount of signing in could fix: every axios call
 * on the page (devices, pings, history) silently recovered via the
 * interceptor, so the device list populated normally while the live stream
 * alone died — and told the admin to "sign in again as the BLE admin", which
 * sent them looking for an account that does not exist. Only once the refresh
 * itself fails is this really denied, and then it is not retried (hammering
 * would not help).
 * After any reconnect the caller gets `onReconnected` so it can backfill the
 * gap from the REST listing; the stream itself only carries new pings.
 */

export type StreamStatus = 'connecting' | 'live' | 'reconnecting' | 'denied' | 'closed';

export interface BleStreamHandlers<T> {
  onPings: (pings: T[]) => void;
  onStatus: (status: StreamStatus) => void;
  /** Fired after a reconnect, or when the server says this viewer dropped pings. */
  onGap: () => void;
}

const MIN_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 30000;

/** Opens the stream; returns a function that closes it for good. */
export function openBleStream<T>(serial: string, handlers: BleStreamHandlers<T>): () => void {
  let closed = false;
  let attempt = 0;
  /**
   * Whether this connect cycle already spent its one refresh. Reset whenever a
   * stream goes live, so a session that outlives several token lifetimes keeps
   * recovering instead of refreshing once and then dying.
   */
  let didRefresh = false;
  let controller: AbortController | null = null;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;

  const url = new URL(`${DEV_NEW_DOMAIN_URL}/ble-pings/admin/stream`, window.location.origin);
  if (serial) url.searchParams.set('serial', serial);

  const dispatch = (event: string, data: string): void => {
    if (event === 'ready') {
      handlers.onStatus('live');
      if (attempt > 0) handlers.onGap();
      attempt = 0;
      didRefresh = false;
    } else if (event === 'pings') {
      try {
        const parsed = JSON.parse(data);
        if (Array.isArray(parsed) && parsed.length > 0) handlers.onPings(parsed as T[]);
      } catch {
        // A malformed frame is skipped; the next one is independent.
      }
    } else if (event === 'dropped') {
      handlers.onGap();
    }
  };

  const scheduleRetry = (): void => {
    if (closed) return;
    handlers.onStatus('reconnecting');
    attempt += 1;
    const base = Math.min(MAX_BACKOFF_MS, MIN_BACKOFF_MS * 2 ** (attempt - 1));
    const delay = base / 2 + Math.random() * (base / 2);
    // eslint-disable-next-line no-use-before-define
    retryTimer = setTimeout(connect, delay);
  };

  async function connect(): Promise<void> {
    if (closed) return;
    controller = new AbortController();
    if (attempt === 0) handlers.onStatus('connecting');
    try {
      const res = await fetch(url.toString(), {
        headers: {
          Accept: 'text/event-stream',
          Authorization: `Bearer ${getTokenFromSessionStorage()}`
        },
        signal: controller.signal,
        cache: 'no-store'
      });
      if (res.status === 401 || res.status === 403) {
        // Most likely just an expired access token — refresh once and retry
        // before concluding this is a permission problem. refreshToken() writes
        // the new token into sessionStorage, which is what the header reads.
        if (!didRefresh && !closed) {
          didRefresh = true;
          const { error: refreshError } = await authenticateService.refreshToken();
          if (!refreshError && !closed) {
            await connect();
            return;
          }
        }
        handlers.onStatus('denied');
        return;
      }
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let event = 'message';
      let data: string[] = [];
      // eslint-disable-next-line no-constant-condition
      while (true) {
        // eslint-disable-next-line no-await-in-loop
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let newline = buffer.indexOf('\n');
        while (newline >= 0) {
          const line = buffer.slice(0, newline).replace(/\r$/, '');
          buffer = buffer.slice(newline + 1);
          if (line === '') {
            if (data.length > 0) dispatch(event, data.join('\n'));
            event = 'message';
            data = [];
          } else if (line.startsWith('event:')) {
            event = line.slice(6).trim();
          } else if (line.startsWith('data:')) {
            data.push(line.slice(5).replace(/^ /, ''));
          }
          // Lines starting with ':' are heartbeats; nothing to do.
          newline = buffer.indexOf('\n');
        }
      }
      // The server ended the stream (its lifetime cap) — reconnect.
      scheduleRetry();
    } catch {
      if (!closed) scheduleRetry();
    }
  }

  connect();

  return () => {
    closed = true;
    if (retryTimer) clearTimeout(retryTimer);
    controller?.abort();
    handlers.onStatus('closed');
  };
}
